import { Form, Switch } from 'antd';
import { useContext } from 'react';

import { RangePicker } from 'app/components/fields/dayjsPickers';
import {
  formatDayjsToTimeRange,
  formatToDayjs,
} from 'app/components/pages/Experiences/Common/_components/FormItems/OpeningHours/utils';
import { ViewEditModeContext } from 'app/context/ViewEditModeContext/ViewEditModeContext';
import type { TimeRange } from 'app/typings/core/admin/packs/openingHours/openingHours';
import { DAY_OF_WEEK } from 'app/typings/core/common/enum/dayOfWeek';
import type { Dayjs } from 'app/utils/dayjs';
import { capitalizeFirstLetter } from 'app/utils/string';

import './OpeningHoursDaily.scss';

type DayjsRangeValue = [Dayjs | null, Dayjs | null] | null;

export const OpeningHoursDaily = () => {
  const form = Form.useFormInstance();
  const openingHours = Form.useWatch('openingHours', form);
  const { isViewMode } = useContext(ViewEditModeContext);

  const days = openingHours?.days ?? {};

  const setDay = (day: string, value: TimeRange | undefined) => {
    form.setFieldValue(['openingHours', 'days'], {
      ...days,
      [day]: value,
    });
  };

  const handleToggle = (day: string, enabled: boolean) => {
    const newDays = { ...days };

    if (!enabled) {
      delete newDays[day];
    } else {
      newDays[day] = {
        start: '09:00',
        end: '18:00',
      };
    }

    form.setFieldValue(['openingHours', 'days'], newDays);
  };

  const handleRangeChange = (day: string, values: DayjsRangeValue) => {
    if (!values) {
      return;
    }

    const [start, end] = values;

    if (!start || !end) {
      return;
    }

    setDay(day, {
      start: start.format('HH:mm'),
      end: end.format('HH:mm'),
    });
  };

  return (
    <div className="daily-opening-hours">
      {Object.values(DAY_OF_WEEK).map((day) => {
        const dayValue = days[day];
        const enabled = !!dayValue;

        return (
          <div key={day} className="daily-opening-hours-day">
            <div className="daily-opening-hours-day-header">
              <Form.Item
                name={['openingHours', 'days', day]}
                valuePropName="checked"
                trigger="onChange"
              >
                <Switch
                  disabled={isViewMode}
                  onChange={(checked) => handleToggle(day, checked)}
                  size="small"
                />
              </Form.Item>

              <span className="day-name">{capitalizeFirstLetter(day)}</span>
            </div>

            {enabled && (
              <div className="picker-wrapper">
                <Form.Item
                  name={['openingHours', 'days', day]}
                  getValueFromEvent={(dayjsRange) =>
                    formatDayjsToTimeRange(dayjsRange)
                  }
                  getValueProps={(value) => {
                    if (
                      Array.isArray(value) &&
                      value.length === 2 &&
                      value[0]?.format &&
                      value[1]?.format
                    ) {
                      return { value };
                    }

                    return {
                      value: value ? formatToDayjs(value) : undefined,
                    };
                  }}
                >
                  <RangePicker
                    picker="time"
                    className="custom-width-picker"
                    format="HH:mm"
                    minuteStep={30}
                    disabled={isViewMode}
                    onChange={(values) => handleRangeChange(day, values)}
                  />
                </Form.Item>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};
